import { UiProgressButton } from '@experiences/ui-common';
import { useRouteResolver } from '@experiences/util';
import {
    Breadcrumbs,
    Button,
    CircularProgress,
    Divider,
    Link,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import Tokens from '@uipath/apollo-core';
import { PortalAlertBar } from '@uipath/portal-shell-react';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { notificationType } from '../../../common/constants/Constant';
import * as RouteNames from '../../../common/constants/RouteNames';
import {
    getSubscriptionProfile,
    SubscriptionProfile,
    updateProfileNotificationSubscription,
} from '../../../services/notification-preferences';
import { accountGlobalId } from '../../../store/selectors';
import UiForm from '../../common/UiForm';
import { UiPanel } from '../../common/UiPanel/UiPanel';
import { useTenantsContext } from '../../tenants/TenantsContextProvider';
import type {
    IProfileSubscriptionPayload,
    IProfileUpdateSubscriptionPayload,
} from '../interfaces/notificationSettings';
import {
    NotificationMode,
    ProfileType,
} from '../interfaces/notificationSettings';
import NotificationTabsComponent from '../NotificationTabsComponent';
import {
    initialNotificationSettingsReducerState,
    notificationSettingsReducer,
} from '../reducers';
import { ActionTypes } from '../reducers/actionTypes';
import NotificationModePreferencesComponent from '../v2/NotificationModePreferencesComponent';

const useStyles = makeStyles(theme =>
    createStyles({
        headerLayout: {
            display: 'flex',
            flexDirection: 'column',
        },
        breadcrumbText: {
            fontSize: Tokens.FontFamily.FontMSize,
            lineHeight: Tokens.FontFamily.FontLinkLineHeight,
            fontWeight: Tokens.FontFamily.FontWeightDefault,
        },
        breadcrumbClickable: {
            cursor: 'pointer',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        breadcrumbLastClickable: {
            cursor: 'pointer',
            color: theme.palette.semantic.colorForeground,
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        cancelButton: { marginRight: '10px' },
        mainContentPlaceholder: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100% - 120px)',
        },
        restoreDefaultSubscriptionBlock: { margin: '20px 0' },
        restoreDefaultSubscriptionLink: {
            marginLeft: Tokens.Padding.PadXxxl,
            fontWeight: Tokens.FontFamily.FontWeightSemibold,
            cursor: 'pointer',
            textDecoration: 'none',
        },
        margintop: { marginTop: '-99px' },
        margintopNoData: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100% - 120px)',
            marginTop: '20px',
        },
        dividerSpacing: {
            marginTop: '-15px',
            marginBottom: '10px',
            color: theme.palette.semantic.colorBorderDeEmp,
        },
    }),
);

const NotificationSettingsEditProfilePage: React.FC<{
    type: string;
    identifier: string;
    displayName: string;
}> = ({
    type, identifier, displayName,
}) => {
    const history = useHistory();
    const accountId = useSelector(accountGlobalId);
    const getRoute = useRouteResolver();
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const {
        control, handleSubmit,
    } = useForm();

    const { selectedTenant: { id: tenantId } } = useTenantsContext();

    const { enqueueSnackbar } = useSnackbar();

    const [ profileSubscriptionsPayload, setProfileSubscriptionsPayload ] = React.useState<IProfileSubscriptionPayload[]>([]);
    const profileRequestUrl = SubscriptionProfile + '?Name=' + identifier;

    const [ isSaveLoading, setisSaveLoading ] = React.useState<boolean>(false);

    const createNotification = useCallback(
        (message: string, notiftype: any) => {
            enqueueSnackbar(message, { variant: notiftype as any });
        },
        [ enqueueSnackbar ],
    );

    const handleProfileError = useCallback(
        (e: Error) => {
            const errorMessage = e?.message;
            createNotification(`${translate({ id: 'CLIENT_NOTIFICATION_PREFERENCES_UPDATE_FAILED' })} ${errorMessage ? ' - ' + errorMessage : ''}`, notificationType.ERROR);
        },
        [ createNotification, translate ],
    );

    const updateSelectedOption = useCallback((data: IProfileSubscriptionPayload[]) => {
        const payloadState = profileSubscriptionsPayload;
        data.forEach((topicItem: IProfileSubscriptionPayload) => {
            const saveItem = payloadState?.find(x => x.topicId === topicItem.topicId
                && x.notificationMode === topicItem.notificationMode);
            if (saveItem !== undefined) {
                saveItem.isMandatory = topicItem.isMandatory;
                saveItem.isSubscribed = topicItem.isSubscribed;
                saveItem.isVisible = topicItem.isVisible;
            } else {
                payloadState?.push(topicItem);
                setProfileSubscriptionsPayload(payloadState);
            }

        });
    }, [ profileSubscriptionsPayload ]);
    const [ publishersData, setPublishersData ] = useState<any>();
    const [ isValidating, setIsValidating ] = useState<boolean>(true);
    const [ error, setError ] = useState<boolean>(false);
    const [ notificationSettings, dispatch ] = React.useReducer(
        notificationSettingsReducer,
        initialNotificationSettingsReducerState,
    );

    useEffect(() => {
        if (accountId && profileRequestUrl && tenantId) {
            const fetchPublishers = async () => {
                try {
                    const data = await getSubscriptionProfile(profileRequestUrl, tenantId, accountId);
                    setPublishersData(data);
                    setIsValidating(false);
                    dispatch({
                        type: ActionTypes.NS_INITIALIZE_NOTIFICATION_SETTINGS,
                        data: [ ...data.publishers ],
                    });
                } catch (err) {
                    setError(true);
                }
            };
            fetchPublishers();
        }
    }, [ accountId, profileRequestUrl, tenantId ]);

    const tabList = useMemo(
        () =>
            notificationSettings
                .publishersWithGroupedTopics
                ?.map((publisher: any) => publisher.displayName),
        [ notificationSettings.publishersWithGroupedTopics ],
    );

    const isPageReady = useMemo(() => tabList.length && !isValidating, [ tabList.length, isValidating ]);
    const noPublishersDataFound = useMemo(
        () => publishersData && notificationSettings.publishers.length === 0,
        [ publishersData, notificationSettings.publishers.length ]);
    const { currentPublisher } = useMemo(
        () => ({ currentPublisher: notificationSettings.publishersWithGroupedTopics[notificationSettings.tabIndex] }),
        [ notificationSettings.publishersWithGroupedTopics, notificationSettings.tabIndex ],
    );
    const showRestoreDefault = useMemo(() => {
        if (currentPublisher !== undefined) {
            let hasChanges = false;
            currentPublisher.modes.forEach((mode) => {
                if (!mode.isSubscribed || mode.isIndeterminate) {
                    hasChanges = true;
                }
            });
            if (hasChanges) {
                return hasChanges;
            }

            currentPublisher.topicGroups.forEach((topicGroup) => {
                for (let i = 0; i < topicGroup.topics.length; i++) {
                    const topic = topicGroup.topics[i];
                    if (topic.isMandatory || !topic.isVisible) {
                        hasChanges = true;
                    }
                }
            });
            return hasChanges;
        }
    }
    , [ currentPublisher, JSON.stringify(currentPublisher?.topicGroups), JSON.stringify(currentPublisher?.modes) ]);

    const showOnlyIfCurrentPublishHasEmail = useCallback(() => {
        if (type === ProfileType.User) {
            return true;
        }
        let hasEmailMode = false;
        currentPublisher.topicGroups.forEach((topicGroup) => {
            if (topicGroup.topics.find(topic => topic.modes!.find(mode => mode.name === NotificationMode.Email)) !== undefined) {
                hasEmailMode = true;
            }
        });
        return hasEmailMode;

    }, [ currentPublisher?.topicGroups, type ]);

    const restoreDefaultSubscriptions = useCallback(async () => {
        dispatch({
            type: ActionTypes.NS_RESTORE_DEFAULT_SUBSCRIPTIONS,
            data: { publisherIndex: currentPublisher.publisherIndex },
        });
        try {
            const payloadState = profileSubscriptionsPayload;
            currentPublisher.topicGroups.forEach((topicGroup) => {
                for (let i = 0; i < topicGroup.topics.length; i++) {
                    const topic = topicGroup.topics[i];
                    topic.modes?.forEach(mode => {

                        const saveItem = payloadState?.find(x => x.topicId === topic.topicId
                            && x.notificationMode === mode.name);
                        if (saveItem !== undefined) {
                            saveItem.isMandatory = false;
                            saveItem.isSubscribed = true;
                            saveItem.isVisible = true;
                        } else {
                            const topicEmailState: IProfileSubscriptionPayload = {
                                topicId: topic.topicId,
                                isSubscribed: true,
                                notificationMode: mode.name,
                                isMandatory: false,
                                isVisible: true,
                            };
                            payloadState?.push(topicEmailState);
                        }
                    });
                }
            });
            setProfileSubscriptionsPayload(payloadState);
            dispatch({
                type: ActionTypes.NS_TOGGLE_PUBLISHER_SUBSCRIPTION_RESET,
                data: {
                    publisherIndex: currentPublisher.publisherIndex,
                    isSubscribed: true,
                },
            });
        } catch (e) {
            handleProfileError(e as Error);
            dispatch({
                type: ActionTypes.NS_RESTORE_DEFAULT_SUBSCRIPTIONS_UPDATE_FAILED,
                data: { publisherIndex: currentPublisher.publisherIndex },
            });
        }
    }, [ currentPublisher?.publisherIndex, currentPublisher?.topicGroups, handleProfileError, profileSubscriptionsPayload ]);

    const SaveProfileSubscription = useCallback(async () => {
        setisSaveLoading(true);
        const saveProfileType = type === ProfileType.NewUserGroup ? ProfileType.UserGroup : type;
        const payloadState = profileSubscriptionsPayload;
        const payLoad: IProfileUpdateSubscriptionPayload = {
            name: identifier,
            description: displayName,
            displayName,
            externalUserGroupId: identifier === 'Default' ? '' : identifier,
            profileType: saveProfileType,
            tenantId,
            profileSubscriptions: payloadState,
        };
        try {
            await updateProfileNotificationSubscription(payLoad, tenantId);
            createNotification(
                translate({ id: 'CLIENT_NS_PROFILE_SUCCESSFULLY_CHANGED' }),
                notificationType.SUCCESS,
            );

            setisSaveLoading(false);
            setProfileSubscriptionsPayload([]);
            history.push(getRoute(RouteNames.NotificationSettings));
        } catch (error) {
            setisSaveLoading(false);
            handleProfileError(error as Error);
        }
    }, [ type, profileSubscriptionsPayload, identifier, displayName, tenantId, currentPublisher?.topicGroups, createNotification, translate, history, getRoute, handleProfileError ]);

    const getNotificationSettingsUiV2 = () => <>

        <NotificationModePreferencesComponent
            {...{
                notificationSettings,
                dispatch,
                handleError: handleProfileError,
                isManagedMode: true,
                updateSelectedOption,
                isUserGroup: type !== ProfileType.User,
            }} />
    </>;
    return (
        <UiPanel
            header={{
                marginTop: '-99px',
                title: (
                    <div className={classes.headerLayout}>
                        <Breadcrumbs separator=">">
                            <span
                                className={clsx(classes.breadcrumbText, classes.breadcrumbClickable)}
                                onClick={() => history.push(getRoute(RouteNames.NotificationSettings))}
                            >
                                {translate({ id: 'CLIENT_MANAGE_NOTIFICATIONS' })}
                            </span>
                            <span
                                className={clsx(classes.breadcrumbText, classes.breadcrumbClickable)}
                                onClick={() => history.push(getRoute(RouteNames.NotificationSettings))}
                            >
                                {translate({ id: 'CLIENT_ALERTING_GROUP_CONFIGURATION' })}
                            </span>
                            <span
                                className={clsx(classes.breadcrumbText, classes.breadcrumbLastClickable)}
                            >
                                {displayName}
                            </span>
                        </Breadcrumbs>
                    </div>
                ),
            }}
            data-cy="edit-profile-panel"

        >
            <Divider className={classes.dividerSpacing} />
            <div>
                {isPageReady ? (<>
                    <UiForm
                        heightOffset="0px"
                        onSubmit={handleSubmit}
                        id="editResource"
                        actions={
                            <div className={classes.actions}>
                                <Button
                                    className={classes.cancelButton}
                                    color="primary"
                                    onClick={() => history.push(getRoute(RouteNames.NotificationSettings))}
                                >
                                    {translate({ id: 'CLIENT_CANCEL' })}
                                </Button>
                                <UiProgressButton
                                    loading={isSaveLoading}
                                    disabled={
                                        profileSubscriptionsPayload.length === 0 && type !== ProfileType.NewUserGroup
                                    }
                                    onClick={() => {
                                        SaveProfileSubscription();
                                    }}
                                    variant="contained"
                                    data-cy="add-edit-submit-button"
                                >
                                    {translate({ id: 'CLIENT_SAVE' })}
                                </UiProgressButton>
                            </div>
                        }
                        centerChild
                    >
                        {showRestoreDefault && (
                            <div className={classes.restoreDefaultSubscriptionBlock}>
                                <PortalAlertBar
                                    status="warning"
                                    cancelable={false}>
                                    <div>
                                        {translate({ id: 'CLIENT_MODIFIED_NOTIFICATION_PREFERENCES' })}
                                        <Link
                                            className={classes.restoreDefaultSubscriptionLink}
                                            onClick={restoreDefaultSubscriptions}>
                                            {translate({ id: 'CLIENT_NOTIFICATION_PREFERENCES_RESTORE_DEFAULT_SUBSCRIPTIONS' })}
                                        </Link>
                                    </div>
                                </PortalAlertBar>
                            </div>
                        ) }
                        <NotificationTabsComponent
                            {...{
                                notificationSettings,
                                tabsList: tabList,
                                dispatch,
                            }}
                        />
                        { showOnlyIfCurrentPublishHasEmail() && getNotificationSettingsUiV2()}
                        { !showOnlyIfCurrentPublishHasEmail() && <>
                            <Divider style={{ marginTop: '1px' }} />
                            <div className={classes.margintopNoData}>
                                <div>
                                    {translate({ id: 'CLIENT_ADMINCONFIG_NO_EMAIL_DATA_FOUND' })}
                                </div>
                            </div>
                        </>}
                    </UiForm>
                </>
                ) : null}
                {!isPageReady && error ? (
                    <div className={classes.mainContentPlaceholder}>
                        <div>
                            {translate({ id: 'CLIENT_FAILED_TO_FETCH_DATA' }) +
                            '. ' +
                            translate({ id: 'CLIENT_TRIAL_REQUESTED_FAILED' })}
                        </div>
                    </div>
                ) : null}
                {(!isPageReady && !error) && noPublishersDataFound ? (
                    <div className={classes.mainContentPlaceholder}>
                        <div>
                            {translate({ id: 'CLIENT_NO_USAGE_DATA_FOUND' })}
                        </div>
                    </div>
                ) : null}
                {(!isPageReady && !error) && !noPublishersDataFound && (
                    <div className={classes.mainContentPlaceholder}>
                        <CircularProgress
                            style={{ margin: '200px' }}
                            thickness={2}
                            size={36} />
                    </div>
                )}
            </div>
        </UiPanel>
    );
};

export default NotificationSettingsEditProfilePage;
